import { useId, useState } from "react";
import { Link } from "react-router";
import { Check, ChevronDown, CircleSlash, Clock, Eye, type LucideIcon } from "lucide-react";
import type { NotificationSpace, SpaceEvent } from "@repo/shared-types";
import { partLabel } from "@/lib/event-form";
import { statusLabel } from "@/lib/events";
import { timeAgo } from "@/lib/inbox";
import { formatMoment } from "@/lib/times";
import { standingTone, type PartTone } from "@/lib/part-tone";
import { cn } from "@/lib/utils";
import { SpacePicture } from "@/components/avatar";
import { CogMenu, MenuButton, MenuLink } from "@/components/cog-menu";
import { EventBanner } from "@/components/event-image";
import { FollowSheet, useFollow } from "@/components/follow";
import { ErrorNote } from "@/components/form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";

/**
 * An event on a feed, a space's and Home's alike, opening its page (0083).
 * Its title is the link, stretched over the whole card, so what sits on top
 * of it can be tapped on its own. One line leads: the space's picture,
 * opening the space, the title, and at the right an eye when the viewer
 * follows it, how long ago it was published and a "…" menu — Follow, or
 * Following… opening the sheet, and Edit event for whoever manages the
 * space's events — the space's name, quieter, under the title. Under it,
 * the card's whole width: the format and the sport as pills; the description, cut short at three lines;
 * and a line with the parts' marks in grey on the left and where the event
 * stands on the right — Live, Upcoming, Ended, or a draft's pill. Its parts
 * wait collapsed behind a strip at its foot; opened, each part says how the
 * viewer's result on it stands in its mark: a part done is muted, one
 * waiting or hidden is not. When it starts and who posted it are on its
 * page (0069).
 */
export function EventCard({
  event,
  base,
  zone,
  space,
  canManage = false,
  now = Date.now(),
}: {
  event: SpaceEvent;
  /** The space's address, which the event's is under. */
  base: string;
  zone: string;
  space?: NotificationSpace;
  /** Whether the viewer may edit the space's events: the menu offers Edit event. */
  canManage?: boolean;
  now?: number;
}) {
  // Only a published event is followed, recorded on, marked and said to be live.
  const published = event.status === "published";
  const follow = useFollow(event, base);
  const [sheet, setSheet] = useState(false);
  const following = published && follow.follow !== null;
  const menu = published || canManage;
  // Collapsed whenever the page draws it; opened, until the page is left.
  const [open, setOpen] = useState(false);
  const list = useId();
  const count = event.parts.length;
  const phase = published && event.phase ? PHASES[event.phase] : undefined;
  const standing = (published && count > 0) || phase || !published;
  return (
    <article className="relative">
      <Card data-event-card className="relative gap-4 transition-shadow hover:shadow-lg">
        {event.image?.url && (
          <div className="px-(--card-spacing) *:rounded-2xl">
            <EventBanner image={event.image} />
          </div>
        )}
        <div className="flex items-center gap-3 px-(--card-spacing)">
          {space && (
            <Link to={base} aria-label={space.name} className="relative z-10 shrink-0">
              <SpacePicture space={space} size="md" />
            </Link>
          )}
          {/* A title of one long word breaks rather than being cut off at a phone's card edge.
              Its link covers the card; the picture, the menu and the strip sit above it. */}
          <div className="min-w-0 flex-1">
            <CardTitle className="text-xl leading-tight font-semibold tracking-tight wrap-anywhere">
              <h2>
                <Link to={`${base}/events/${event.id}`} data-event-link className="after:absolute after:inset-0 after:content-['']">
                  {event.title}
                </Link>
              </h2>
            </CardTitle>
            {/* The space under the title, quieter, beside its picture. */}
            {space && <p className="truncate text-xs text-muted-foreground">{space.name}</p>}
          </div>
          {menu && (
            <div className="relative z-10 -mr-2 flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
              {following && <Eye className="size-3.5" role="img" aria-label="Following" />}
              {published && event.publishedAt && <span>{timeAgo(event.publishedAt, now)}</span>}
              {menu && (
                <CogMenu label={`${event.title} menu`} compact more>
                  {(close) => (
                    <>
                      {published && (
                        <MenuButton
                          onClick={() => {
                            close();
                            if (following) setSheet(true);
                            else follow.start();
                          }}
                        >
                          {following ? "Following…" : "Follow"}
                        </MenuButton>
                      )}
                      {canManage && <MenuLink to={`${base}/events/${event.id}/edit`} label="Edit event" />}
                    </>
                  )}
                </CogMenu>
              )}
            </div>
          )}
        </div>
        <div className="space-y-3 px-(--card-spacing)">
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="secondary">{event.formatLabel}</Badge>
            {event.sportLabel && <Badge variant="secondary">{event.sportLabel}</Badge>}
          </div>
          {event.description && (
            <p className="line-clamp-3 text-sm whitespace-pre-line text-muted-foreground">{event.description}</p>
          )}
          {standing && (
            <div className="flex min-h-5 items-center justify-between gap-3">
              {published && count > 0 ? (
                // Where the viewer stands on each part at a glance; opening the card says it in words.
                <span aria-hidden className="flex flex-wrap items-center gap-1">
                  {event.parts.map((part) => {
                    const mark = markOf(part.viewerStanding);
                    return mark ? <mark.icon key={part.id} className="size-3.5 text-muted-foreground" /> : <ToDo key={part.id} />;
                  })}
                </span>
              ) : (
                <span />
              )}
              {published ? (
                phase && (
                  <span className={cn("text-sm", event.phase === "live" ? "font-medium text-success" : "text-muted-foreground")}>
                    {phase}
                  </span>
                )
              ) : (
                <Badge className="bg-warning-muted text-warning">
                  {statusLabel(event.status)}
                  {event.status === "scheduled" && event.publishedAt && ` · ${formatMoment(event.publishedAt, zone)}`}
                </Badge>
              )}
            </div>
          )}
        </div>
        {follow.error && !sheet && (
          <div className="relative z-10 px-(--card-spacing)">
            <ErrorNote message={follow.error} />
          </div>
        )}
        {count > 0 && (
          // Down to the card's edge: opened, the parts under a hairline as wide as the text; and,
          // open or not, a darker strip along the foot, the chevron in its middle.
          <div className="-mb-(--card-spacing)">
            {open && (
              <div className="pb-3">
                <div className="mx-(--card-spacing) mb-4 border-t" />
                <CardContent>
                  <ul id={list} className="space-y-2 text-sm">
                    {event.parts.map((part, index) => {
                      const mark = published ? markOf(part.viewerStanding) : null;
                      return (
                        <li key={part.id} className={cn("flex items-start gap-2", mark?.done && "text-muted-foreground")}>
                          {published && (
                            // Level with the name's line, 20px tall.
                            <span className="flex h-5 w-3.5 shrink-0 items-center justify-center">
                              {mark ? <mark.icon aria-hidden className="size-3.5 text-muted-foreground" /> : <ToDo />}
                            </span>
                          )}
                          {/* Two lines: the part's name, then how it is scored; each cut short on its own. */}
                          <span className="min-w-0 flex-1">
                            {/* The name, and at the line's right end how the viewer's result on it stands. */}
                            <span className="flex items-baseline justify-between gap-3">
                              <span className={cn("truncate", !mark?.done && "text-foreground")}>
                                {partLabel(index)}. {part.title}
                              </span>
                              {published && (
                                <span className="shrink-0 text-xs text-muted-foreground">{labelOf(part.viewerStanding)}</span>
                              )}
                            </span>
                            <span className="block truncate text-xs text-muted-foreground">{part.summary}</span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </CardContent>
              </div>
            )}
            {/* Above the card's link, as the menu is. */}
            <div className="relative z-10 flex">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                // The whole strip, its fill its own rather than a menu trigger's.
                className="h-9 flex-1 justify-center rounded-none bg-muted text-muted-foreground hover:bg-muted aria-expanded:bg-muted aria-expanded:text-muted-foreground dark:bg-background/40 dark:hover:bg-background/40 dark:aria-expanded:bg-background/40"
                aria-expanded={open}
                // The list it opens, while it is there to point at.
                aria-controls={open ? list : undefined}
                aria-label={open ? "Hide parts" : count === 1 ? "Show 1 part" : `Show ${count} parts`}
                onClick={() => setOpen(!open)}
              >
                <ChevronDown
                  aria-hidden
                  strokeWidth={2.5}
                  className={cn("size-5.5 transition-transform motion-reduce:transition-none", open && "rotate-180")}
                />
              </Button>
            </div>
          </div>
        )}
      </Card>
      {sheet && follow.follow && (
        <FollowSheet event={event} control={follow} follow={follow.follow} onClose={() => setSheet(false)} />
      )}
    </article>
  );
}

const PHASES: Record<string, string> = { upcoming: "Upcoming", live: "Live", ended: "Ended" };

/** A part the viewer has not recorded: a grey ring, the size of a mark's circle. */
function ToDo() {
  return <span aria-hidden className="size-3 rounded-full border-[1.5px] border-muted-foreground/60" />;
}

const MARKS: Record<PartTone, { icon: LucideIcon; done: boolean }> = {
  ok: { icon: Check, done: true },
  waiting: { icon: Clock, done: false },
  bad: { icon: CircleSlash, done: false },
};

const LABELS: Record<string, string> = {
  verified: "Verified",
  unverified: "Unverified",
  waiting: "Late",
  hidden: "Hidden",
  "": "Done",
};

/**
 * How the viewer's result on a part stands, in a word, at the right of its
 * name in the opened list: the api's standing, "Late" for one changed or
 * shared after the deadline and off the board until verified, "Done" for one
 * on a part with nothing to verify, or private. A standing this build does
 * not know is still a result recorded.
 */
function labelOf(standing: string | null): string {
  if (standing === null) return "Not done";
  return LABELS[standing] ?? "Done";
}

/**
 * How the viewer's result on a part stands, by the event page's rule (0070),
 * as a grey mark: done, waiting for verification, or hidden — each a shape
 * of its own, and said in a word beside the part where the parts are listed. Only a part done is muted: one waiting or hidden still asks
 * something of somebody. Null while nothing is recorded: a part to do, a
 * grey ring. A standing this
 * build does not know is still a result recorded, so it reads done.
 */
function markOf(standing: string | null) {
  if (standing === null) return null;
  return MARKS[standingTone(standing) ?? "ok"];
}
